/**
 * Abstraction for storing changelog "last read" state per user.
 * Uses API (Redis-backed) for cross-device sync; localStorage available as fallback.
 */

import axios from 'axios';

const STORAGE_KEY = 'changelog_last_read_at';

/**
 * API adapter: stores last read in Redis via backend. Cross-device sync.
 */
const apiChangelogStorageAdapter = {
    getLastReadAt(userId) {
        return axios
            .get('/api/v1/changelog/read')
            .then(res => res.data?.lastReadAt || null)
            .catch(() => null);
    },

    setLastReadAt(userId, timestamp) {
        return axios.post('/api/v1/changelog/read', { lastReadAt: timestamp }).catch(() => {});
    },
};

/**
 * localStorage adapter: stores { [userId]: "ISO8601" } per user on same device.
 * Fallback when API is unavailable.
 */
const localStorageAdapter = {
    getLastReadAt(userId) {
        if (!userId) return Promise.resolve(null);
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            if (!raw) return Promise.resolve(null);
            const data = JSON.parse(raw);
            return Promise.resolve(data[String(userId)] || null);
        } catch {
            return Promise.resolve(null);
        }
    },

    setLastReadAt(userId, timestamp) {
        if (!userId) return Promise.resolve();
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY) || '{}';
            const data = JSON.parse(raw);
            data[String(userId)] = timestamp;
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch {
            // ignore
        }
        return Promise.resolve();
    },
};

/**
 * Combined adapter: writes to both localStorage and API (Redis).
 * On read, returns the most recent timestamp from either source so that
 * data survives Redis restarts (localStorage) and works cross-device (API).
 */
export const changelogStorage = {
    getLastReadAt(userId) {
        return Promise.all([
            localStorageAdapter.getLastReadAt(userId),
            apiChangelogStorageAdapter.getLastReadAt(userId),
        ]).then(([localTs, apiTs]) => {
            if (!localTs) return apiTs;
            if (!apiTs || new Date(localTs) > new Date(apiTs)) {
                apiChangelogStorageAdapter.setLastReadAt(userId, localTs);
                return localTs;
            }
            return apiTs;
        });
    },

    setLastReadAt(userId, timestamp) {
        localStorageAdapter.setLastReadAt(userId, timestamp);
        return apiChangelogStorageAdapter.setLastReadAt(userId, timestamp);
    },
};
